import { supabase } from '../../../src/lib/supabase';
import {
  MESES_ORDEM,
  gerarUltimosMeses,
  limparUc,
  mesReferenciaParaOrdinal,
  normalizarMesReferencia,
} from './mesReferenciaRateio';

// Os helpers puros de mês/UC vivem em `mesReferencia.ts` desde que este módulo
// passou a ser consumido por código coberto por teste: o `import` do cliente
// Supabase logo acima torna qualquer coisa daqui impossível de rodar em
// `node --test` sem subir rede/env. Comportamento inalterado — a re-exportação
// abaixo mantém a API pública deste arquivo exatamente como estava.
export { MESES_ORDEM, gerarUltimosMeses, limparUc, mesReferenciaParaOrdinal, normalizarMesReferencia };

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Próxima ocorrência de `diaLeituraPadrao` (1-31) a partir de `vinculadoEm`
 * (Seção 10 — previsão de recebimento, marco "Leitura"). Aritmética pura de
 * data (sem Intl), mesmo estilo de gerarUltimosMeses. Retorna null se faltar
 * algum dos dois inputs — nunca inventa uma data sem base real.
 */
export function calcularProximaLeitura(
  vinculadoEm: string | null | undefined,
  diaLeituraPadrao: number | null | undefined
): Date | null {
  if (!vinculadoEm || !diaLeituraPadrao || diaLeituraPadrao < 1 || diaLeituraPadrao > 31) return null;
  const inicio = new Date(vinculadoEm);
  if (Number.isNaN(inicio.getTime())) return null;

  let candidata = new Date(inicio.getFullYear(), inicio.getMonth(), diaLeituraPadrao);
  if (candidata < inicio) {
    candidata = new Date(inicio.getFullYear(), inicio.getMonth() + 1, diaLeituraPadrao);
  }
  return candidata;
}

// ── Calendário de risco da Unidade Âncora (Seção 14) ───────────────────────

export const JANELA_RISCO_LEITURA_DIAS = 3;
export type StatusDiaLeitura = 'leitura' | 'risco' | 'normal';

const diasNoMes = (ano: number, mesIndex: number) => new Date(ano, mesIndex + 1, 0).getDate();

/**
 * Classifica um dia quanto à proximidade do dia de leitura da distribuidora
 * (Seção 14 — calendário de risco da Âncora). Considera a leitura do mês da
 * própria data e dos meses vizinhos (o grid mostra dias de borda de outros
 * meses). JANELA_RISCO_LEITURA_DIAS é a margem antes/depois considerada
 * arriscada — não existe essa janela no schema (só o dia fixo do mês), então
 * fica como constante aqui, decisão tomada com o usuário.
 */
export function classificarDiaLeitura(
  data: Date,
  diaLeituraPadrao: number | null | undefined,
  janelaDias: number = JANELA_RISCO_LEITURA_DIAS
): StatusDiaLeitura {
  if (!diaLeituraPadrao || diaLeituraPadrao < 1 || diaLeituraPadrao > 31) return 'normal';
  for (const offset of [-1, 0, 1]) {
    const ano = data.getFullYear();
    const mesIndex = data.getMonth() + offset;
    const dia = Math.min(diaLeituraPadrao, diasNoMes(ano, mesIndex));
    const leitura = new Date(ano, mesIndex, dia);
    const diffDias = Math.round((data.getTime() - leitura.getTime()) / 86400000);
    if (diffDias === 0) return 'leitura';
    if (Math.abs(diffDias) <= janelaDias) return 'risco';
  }
  return 'normal';
}

// ── Busca em lote do histórico de faturas por UC ──────────────────────────

export type FaturaConsumoResumo = {
  uc_codigo: string;
  mes_referencia: string; // já normalizado
  consumo_ativo_kwh: number;
  /**
   * `faturas.saldo_acumulado_kwh` — apesar do nome, é o resultado DO MÊS
   * (excedente recebido − crédito recebido), não o estoque de créditos.
   */
  saldo_acumulado_kwh: number | null;
  /**
   * `faturas.saldo_ugs_vigor` — o "SALDO KWH" do bloco SCEE, que é o ESTOQUE de
   * créditos da UC na distribuidora. É esse que responde "quantos meses de
   * consumo o saldo cobre" (ver `coberturaSaldo.ts`); o de cima, não.
   */
  saldo_creditos_kwh: number | null;
  /** `faturas.credito_recebido` — kWh de crédito que chegou na UC no mês (painel Saldos das UCs). */
  credito_recebido_kwh?: number | null;
  /** `faturas.energia_injetada_gd1_kwh` — o crédito das usinas da Vigor injetado na UC. */
  energia_injetada_kwh?: number | null;
};

/**
 * Para uma lista de UCs, busca em lote (uc_codigo indexado — ver migration
 * 20260515120000) o histórico de faturas dos últimos `mesesLookback` meses,
 * ignorando faturas AVULSO e status 'Excluído' (filtro NULL-safe — ver nota
 * abaixo). Retorna Map ucId -> faturas ordenadas da mais recente pra mais
 * antiga. Só LÊ `faturas`; nunca escreve em unidades_consumidoras.consumo_medio_kwh.
 */
export async function buscarHistoricoConsumoUcs(
  ucs: { id: number; numero_uc: string; outro_numero_uc?: string | null }[],
  opts: { mesesLookback?: number } = {}
): Promise<Map<number, FaturaConsumoResumo[]>> {
  const resultado = new Map<number, FaturaConsumoResumo[]>();
  if (ucs.length === 0) return resultado;

  const codigoParaUcId = new Map<string, number>();
  for (const u of ucs) {
    const c1 = limparUc(u.numero_uc);
    const c2 = limparUc(u.outro_numero_uc);
    if (c1) codigoParaUcId.set(c1, u.id);
    if (c2) codigoParaUcId.set(c2, u.id);
    resultado.set(u.id, []);
  }

  const codigos = Array.from(codigoParaUcId.keys());
  const mesesJanela = gerarUltimosMeses(opts.mesesLookback ?? 12);

  // Lote de 200 códigos por vez — evita URL enorme em UGs com muitas UCs.
  const LOTE = 200;
  const lotes: string[][] = [];
  for (let i = 0; i < codigos.length; i += LOTE) lotes.push(codigos.slice(i, i + LOTE));

  // PAGINAÇÃO OBRIGATÓRIA: o PostgREST corta a resposta em 1.000 linhas, sem erro
  // e sem aviso. Um lote de 200 códigos × 12 meses passa disso numa UG grande, e o
  // histórico voltava truncado — o delta e a cobertura saíam de dados parciais, com
  // o corte mudando a cada carregamento da tela.
  const PAGINA = 1000;
  const respostas = await Promise.all(
    lotes.map(async lote => {
      const linhas: unknown[] = [];
      let offset = 0;
      for (;;) {
        const { data, error } = await supabase
          .from('faturas')
          .select('uc_codigo, mes_referencia, consumo_ativo_kwh, saldo_acumulado_kwh, saldo_ugs_vigor, credito_recebido, energia_injetada_gd1_kwh')
          .in('uc_codigo', lote)
          .in('mes_referencia', mesesJanela)
          // NULL-safe: .neq() sozinho excluiria linhas com classificacao/status_fatura NULL
          // (classificacao é a string tarifária completa, ex. "RESIDENCIAL NORMAL
          // CONVENCIONAL" — não um enum limpo — e pode ser NULL; mesmo risco em status_fatura).
          .or('classificacao.neq.AVULSO,classificacao.is.null')
          .or('status_fatura.neq.Excluído,status_fatura.is.null')
          // Ordem estável: sem ela a paginação pode repetir ou pular linhas.
          .order('id', { ascending: true })
          .range(offset, offset + PAGINA - 1);
        if (error) return { data: null, error };
        linhas.push(...(data || []));
        if (!data || data.length < PAGINA) break;
        offset += PAGINA;
      }
      return { data: linhas, error: null };
    })
  );

  for (const { data, error } of respostas) {
    if (error) throw error;
    for (const f of data || []) {
      const ucId = codigoParaUcId.get(limparUc((f as any).uc_codigo));
      if (ucId == null) continue;
      resultado.get(ucId)!.push({
        uc_codigo: (f as any).uc_codigo,
        mes_referencia: normalizarMesReferencia((f as any).mes_referencia),
        consumo_ativo_kwh: (f as any).consumo_ativo_kwh,
        saldo_acumulado_kwh: (f as any).saldo_acumulado_kwh ?? null,
        saldo_creditos_kwh: (f as any).saldo_ugs_vigor ?? null,
        credito_recebido_kwh: (f as any).credito_recebido ?? null,
        energia_injetada_kwh: (f as any).energia_injetada_gd1_kwh ?? null,
      });
    }
  }

  for (const lista of resultado.values()) {
    lista.sort((a, b) => (mesReferenciaParaOrdinal(b.mes_referencia) ?? -1) - (mesReferenciaParaOrdinal(a.mes_referencia) ?? -1));
  }

  return resultado;
}

// ── Margem de Erro ──────────────────────────────────────────────────────
// Valor fixo, igual pra todas as UCs — mesmo critério da planilha antiga
// (célula única "Margem de erro: 15%"), não mais um cálculo estatístico por UC.
export const MARGEM_ERRO_PADRAO_PERCENT = 15;

// ── Delta (variação consumo mês atual vs. consumo_medio_kwh cadastrado) ────
//
// A regra desceu para `supabase/functions/_shared/variacaoConsumo.ts` porque o
// vigia da Vick dispara este mesmo alerta e não alcança `views/`. Reexportado
// aqui para quem já importava daqui continuar funcionando.
export {
  LIMIAR_ALERTA_DELTA_PERCENT,
  calcularDelta,
} from '../../../supabase/functions/_shared/variacaoConsumo.ts';
export type { DeltaConsumo } from '../../../supabase/functions/_shared/variacaoConsumo.ts';

// ── Cobertura (meses de consumo que o saldo acumulado cobre) — Travamento Automático ──
// Seção 9/13.3 da spec: cobertura > 3 meses aciona travamento automático da UC.
// Conceito diferente do "% Cobertura da Carteira" do Dashboard (DashboardRateioView.tsx),
// que é geração atendendo consumo agregado — este aqui é por UC, saldo ÷ consumo médio.
//
// ⚠️ NÃO é o caminho vivo. Quem decide o travamento hoje é `avaliarTravamento`
// (src/lib/consumoOperacional.ts), com as travas de confiança do consumo, e o
// alerta de saldo BAIXO é `coberturaSaldo.ts`. Estas duas linhas permanecem só
// porque a spec as cita pelo nome; nenhum código chama `calcularCobertura`.

export type CoberturaResultado = { coberturaMeses: number; travamentoRecomendado: boolean };

export const LIMIAR_TRAVAMENTO_COBERTURA_MESES = 3;

export function calcularCobertura(
  saldoAcumuladoKwh: number | null | undefined,
  consumoMedioKwh: number | null | undefined
): CoberturaResultado | null {
  if (saldoAcumuladoKwh == null || consumoMedioKwh == null || consumoMedioKwh <= 0) return null;
  const coberturaMeses = round2(saldoAcumuladoKwh / consumoMedioKwh);
  return { coberturaMeses, travamentoRecomendado: coberturaMeses > LIMIAR_TRAVAMENTO_COBERTURA_MESES };
}

// ── Percentual utilizado no rateio anterior (referência histórica, só leitura) ──

/**
 * Pra cada UC, busca o `percent_rateio` do rateio mais recente ANTES de
 * `mesReferenciaAtual` nessa mesma UG (equivalente à coluna "Últ. Vigente (%)"
 * da planilha antiga) — puramente informativo, não alimenta nenhum cálculo.
 */
export async function buscarPercentualAnteriorUcs(
  ugId: string,
  ucIds: number[],
  mesReferenciaAtual: string
): Promise<Map<number, number>> {
  const resultado = new Map<number, number>();
  if (ucIds.length === 0) return resultado;

  const { data, error } = await supabase
    .from('rateio_itens')
    .select('unidade_consumidora_id, percent_rateio, rateios!inner(mes_referencia, rateio_unidade_geradora_id)')
    .in('unidade_consumidora_id', ucIds)
    .eq('rateios.rateio_unidade_geradora_id', ugId);
  if (error) throw error;

  const ordinalAtual = mesReferenciaParaOrdinal(mesReferenciaAtual);
  const melhorOrdinalPorUc = new Map<number, number>();

  for (const item of (data || []) as any[]) {
    const ucId = item.unidade_consumidora_id as number;
    const mesRef = item.rateios?.mes_referencia as string | undefined;
    const ordinal = mesReferenciaParaOrdinal(mesRef);
    if (ordinal == null) continue;
    if (ordinalAtual != null && ordinal >= ordinalAtual) continue; // só rateios ANTERIORES ao atual
    const melhorAtual = melhorOrdinalPorUc.get(ucId);
    if (melhorAtual == null || ordinal > melhorAtual) {
      melhorOrdinalPorUc.set(ucId, ordinal);
      resultado.set(ucId, item.percent_rateio as number);
    }
  }

  return resultado;
}

export type UltimoPercentualUc = { percent: number; mesReferencia: string };

/**
 * Igual a buscarPercentualAnteriorUcs, mas sem filtro de UG nem de "mês
 * atual" — pega o item de rateio mais recente de cada UC entre TODAS as UGs
 * em que ela já entrou (útil pra Unidade Âncora, que não tem um "mês atual"
 * de referência fixo). Como uma UC só pode estar vinculada a uma UG por vez
 * (rateio_unidade_geradora_id), na prática isso reflete o histórico da UG
 * vinculada no momento.
 */
export async function buscarUltimoPercentualPorUc(ucIds: number[]): Promise<Map<number, UltimoPercentualUc>> {
  const resultado = new Map<number, UltimoPercentualUc>();
  if (ucIds.length === 0) return resultado;

  const { data, error } = await supabase
    .from('rateio_itens')
    .select('unidade_consumidora_id, percent_rateio, rateios!inner(mes_referencia)')
    .in('unidade_consumidora_id', ucIds);
  if (error) throw error;

  const melhorOrdinalPorUc = new Map<number, number>();

  for (const item of (data || []) as any[]) {
    const ucId = item.unidade_consumidora_id as number;
    const mesRef = item.rateios?.mes_referencia as string | undefined;
    const ordinal = mesReferenciaParaOrdinal(mesRef);
    if (ordinal == null) continue;
    const melhorAtual = melhorOrdinalPorUc.get(ucId);
    if (melhorAtual == null || ordinal > melhorAtual) {
      melhorOrdinalPorUc.set(ucId, ordinal);
      resultado.set(ucId, { percent: item.percent_rateio as number, mesReferencia: normalizarMesReferencia(mesRef) });
    }
  }

  return resultado;
}
